import { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Inline,
  InlineItem,
  Button,
  Input,
  Stack,
  Text,
  Secondary,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';

const parseRedirectUris = (raw: string): string[] =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const ConnectorCreateModal = (props: ConnectorCreateModalProps) => {
  const {
    onCancel = noop,
    onCreate = noop,
    isCreating = false,
    animation = true,
  } = props;
  const [name, setName] = useState('');
  const [redirectUrisText, setRedirectUrisText] = useState('');

  const redirectUris = useMemo(
    () => parseRedirectUris(redirectUrisText),
    [redirectUrisText],
  );

  const handleCreate = useCallback(() => {
    onCreate(name.trim(), redirectUris);
  }, [onCreate, name, redirectUris]);

  return (
    <Modal
      aria-label='Add connector'
      width={560}
      animation={animation}
      onClose={onCancel}
    >
      <ModalHeader>Add connector</ModalHeader>
      <ModalBody>
        <Stack space='medium'>
          <Text>
            <Secondary>
              Create a client credential for an AI connector (e.g. M365 Copilot,
              Gemini). Paste the resulting client ID and secret into the
              connector&rsquo;s configuration.
            </Secondary>
          </Text>
          <Input
            label='Name'
            placeholder='e.g. M365 Copilot, Gemini Enterprise'
            value={name}
            onChange={setName}
            magnitude='large'
          />
          <Stack space='xsmall'>
            <Input
              label='Redirect URIs'
              placeholder='https://teams.microsoft.com/api/platform/v1.0/oAuthRedirect'
              value={redirectUrisText}
              onChange={setRedirectUrisText}
              magnitude='large'
              multiline
            />
            <Text>
              <Secondary>
                One per line. Must be HTTPS (or loopback http).
              </Secondary>
            </Text>
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Inline>
          <InlineItem sizing='fill-container' />
          <InlineItem>
            <Button variant='secondary' onClick={onCancel}>
              Cancel
            </Button>
          </InlineItem>
          <InlineItem>
            <Button
              onClick={handleCreate}
              disabled={
                isCreating ||
                name.trim().length === 0 ||
                redirectUris.length === 0
              }
            >
              Add connector
            </Button>
          </InlineItem>
        </Inline>
      </ModalFooter>
    </Modal>
  );
};

type ConnectorCreateModalProps = {
  onCancel?: () => void;
  onCreate?: (name: string, redirectUris: string[]) => void;
  isCreating?: boolean;
  animation?: boolean;
};

export type { ConnectorCreateModalProps };
export { ConnectorCreateModal };
